import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { CircleAlert, Package, SlidersHorizontal, Tag, UserRound } from 'lucide-react';
import { MinimalProductDto } from 'api/product';
import { TypeTunnel } from 'api/funnel';
import { funnelsAPI, productsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { ModalShell, ModalSection } from 'back-office/shared/components/ModalShell';
import { TYPE_TUNNEL_OPTIONS } from '../detail/funnelEnums';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { useTunnelBeneficiaryOptions } from '../detail/hooks/useActorOptions';
import { useBankAccountOptions } from '../detail/hooks/useBankAccountOptions';

interface CreateTunnelFormValues {
  code: string;
  designation: string;
  typeTunnel: TypeTunnel;
  produitId: string;
  beneficiairePersonneId: string;
  compteBancaireId: string;
  fraisResiliation: number | '';
  brokerCollectionAllowed: boolean;
}

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .trim()
    .required('Le code est requis')
    .max(64, 'Le code ne peut pas dépasser 64 caractères'),
  designation: Yup.string()
    .trim()
    .required('La désignation est requise')
    .max(200, 'La désignation ne peut pas dépasser 200 caractères'),
  produitId: Yup.string().required('Le produit est requis'),
  beneficiairePersonneId: Yup.string().required('Le bénéficiaire est requis'),
  compteBancaireId: Yup.string().required('Le compte bancaire du bénéficiaire est requis'),
  fraisResiliation: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .min(0, 'Les frais de résiliation ne peuvent pas être négatifs')
    .nullable(),
});

const initialValues: CreateTunnelFormValues = {
  code: '',
  designation: '',
  typeTunnel: TypeTunnel.Individuel,
  produitId: '',
  beneficiairePersonneId: '',
  compteBancaireId: '',
  fraisResiliation: '',
  brokerCollectionAllowed: false,
};

interface CreateTunnelModalProps {
  /** Rafraîchissement de la liste, où le tunnel apparaît inactif. */
  onCreated?: () => void;
}

/**
 * Création d'un tunnel vierge.
 *
 * Ne demande que ce qu'un tunnel ne peut pas déduire : son code, son libellé, le produit
 * qu'il vend et qui en encaisse la prime. Garanties, frais, supports et variables se
 * paramètrent ensuite sur la fiche — d'où la redirection après création, et d'où le
 * tunnel créé désactivé : un tunnel sans garantie n'a rien à faire en souscription.
 */
export const CreateTunnelModal: FC<CreateTunnelModalProps> = ({ onCreated }) => {
  const formikRef = useRef<FormikProps<CreateTunnelFormValues>>(null);
  const navigate = useNavigate();
  const { closeDialog } = useDialog();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Le back plafonne la page à 100 : au-delà, les produits les plus récents manqueraient
  // à la liste. Ils se comptent aujourd'hui en dizaines.
  const fetchProducts = useCallback(
    () => productsAPI.getProducts({ getGenericListCommandDto: { pageNumber: 1, pageSize: 100 } }),
    []
  );
  const products = useAsyncResource(fetchProducts);

  const beneficiaries = useTunnelBeneficiaryOptions();

  const productItems = useMemo(
    () =>
      (products.data?.items ?? [])
        .map((product: MinimalProductDto) => ({
          key: product.id,
          label: product.label,
          trailingLabel: product.code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [products.data]
  );

  const handleSubmit = async (values: CreateTunnelFormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await funnelsAPI.createFunnel({
        createTunnelCommand: {
          code: values.code.trim(),
          designation: values.designation.trim(),
          // Toujours envoyé : le champ est optionnel côté back, mais un tunnel collectif
          // ne se requalifie pas ensuite — c'est la création qui fixe le parcours.
          typeTunnel: values.typeTunnel,
          produitId: values.produitId,
          beneficiairePersonneId: values.beneficiairePersonneId,
          compteBancaireId: values.compteBancaireId,
          // Un tunnel neuf n'a ni garantie ni tarif : il s'active depuis sa fiche.
          isActive: false,
          fraisResiliation:
            values.fraisResiliation === '' ? null : Number(values.fraisResiliation),
          brokerCollectionAllowed: values.brokerCollectionAllowed,
        },
      });

      if (!response.isSuccess) {
        // Code déjà pris, produit introuvable… : message rédigé par le back.
        setError(response.errorMessage || 'La création du tunnel a échoué.');
        return;
      }

      toast.success('Tunnel créé');
      onCreated?.();
      closeDialog();

      // Le paramétrage se poursuit sur la fiche : garanties, frais, supports, variables.
      if (response.id) navigate(`/back-office/tunnels/${response.id}`);
    } catch (thrown) {
      console.error('Création du tunnel impossible', thrown);
      const parsed = await parseApiError(thrown);
      setError(parsed.message || 'La création du tunnel a échoué.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <ModalShell
        title="Créer un tunnel"
        subtitle="Le paramétrage se poursuit sur la fiche du tunnel"
        isLoading={products.isLoading}
        submit={{
          label: isSubmitting ? 'Création…' : 'Créer le tunnel',
          onClick: () => formikRef.current?.submitForm(),
          disabled: isSubmitting,
        }}
        closeLabel="Annuler"
      >
        <Form>
          {error && (
            <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <ModalSection title="Identité" icon={<Tag className="h-4 w-4" />}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <FormikInputText
                  name="code"
                  label="Code"
                  required
                  placeholder="ex. CBNO_Coreis"
                  disabled={isSubmitting}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Identifie le tunnel partout (API partenaires, imports, paramétrage) et ne
                  sera plus modifiable ensuite. Il doit être libre.
                </p>
              </div>
              <FormikInputText
                name="designation"
                label="Désignation"
                required
                placeholder="ex. CBNO Coreis 2027"
                disabled={isSubmitting}
              />
              <div>
                <FormikDropdown
                  name="typeTunnel"
                  label="Type de tunnel"
                  required
                  items={TYPE_TUNNEL_OPTIONS.map((option) => ({
                    key: option.value,
                    label: option.label,
                    trailingLabel: option.hint,
                  }))}
                  disabled={isSubmitting}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Structure tout le parcours et n'est plus modifiable : un tunnel collectif ne
                  se souscrit qu'au travers d'un contrat de groupe, et n'accepte que les
                  supports GROUP / CADRE.
                </p>
              </div>
            </div>
          </ModalSection>

          <ModalSection
            title="Produit"
            icon={<Package className="h-4 w-4" />}
            subtitle="Ce que le tunnel vend"
          >
            <FormikDropdown
              name="produitId"
              label="Produit"
              required
              items={productItems}
              disabled={isSubmitting}
              placeholder={
                products.isLoading
                  ? 'Chargement…'
                  : productItems.length
                    ? 'Sélectionner un produit'
                    : 'Aucun produit disponible'
              }
            />
          </ModalSection>

          <ModalSection
            title="Bénéficiaire"
            icon={<UserRound className="h-4 w-4" />}
            subtitle="Qui encaisse la prime"
          >
            <BeneficiaryFields
              // Pas de rôle en libellé secondaire : la liste est dédoublonnée par
              // personne, un acteur à la fois assureur et grossiste n'en afficherait
              // qu'un des deux, au hasard de l'ordre de chargement.
              options={(beneficiaries.data ?? []).map((actor) => ({
                key: actor.key,
                label: actor.label,
              }))}
              isLoading={beneficiaries.isLoading}
              disabled={isSubmitting}
            />
          </ModalSection>

          <ModalSection title="Options" icon={<SlidersHorizontal className="h-4 w-4" />}>
            <div className="flex flex-col gap-4">
              <FormikInputText
                name="fraisResiliation"
                label="Frais de résiliation (€)"
                type="number"
                placeholder="0"
                disabled={isSubmitting}
              />
              <div className="flex flex-col gap-4 rounded-lg bg-gray-50 p-4">
                <FormikSwitch name="brokerCollectionAllowed" label="Encaissement par le courtier autorisé" />
              </div>
              <p className="text-xs text-gray-400">
                Le tunnel est créé <span className="font-semibold">désactivé</span> : ajoutez
                ses garanties et ses frais sur sa fiche, puis activez-le.
              </p>
            </div>
          </ModalSection>
        </Form>
      </ModalShell>
    </Formik>
  );
};

/**
 * Bénéficiaire et son compte bancaire. Les comptes se rechargent à chaque changement de
 * titulaire, et celui marqué par défaut est présélectionné — sans quoi l'admin doit
 * rouvrir une liste d'un seul élément à chaque création.
 */
const BeneficiaryFields: FC<{
  options: { key: string; label: string; trailingLabel?: string }[];
  isLoading: boolean;
  disabled?: boolean;
}> = ({ options, isLoading, disabled }) => {
  const { values, setFieldValue } = useFormikContext<CreateTunnelFormValues>();
  const bankAccounts = useBankAccountOptions(values.beneficiairePersonneId || null);

  useEffect(() => {
    if (!values.compteBancaireId && bankAccounts.defaultAccountId) {
      void setFieldValue('compteBancaireId', bankAccounts.defaultAccountId);
    }
  }, [bankAccounts.defaultAccountId, values.compteBancaireId, setFieldValue]);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormikDropdown
        name="beneficiairePersonneId"
        label="Bénéficiaire"
        required
        items={options}
        disabled={disabled}
        placeholder={
          isLoading
            ? 'Chargement…'
            : options.length
              ? 'Sélectionner un bénéficiaire'
              : 'Aucun acteur disponible'
        }
      />
      <FormikDropdown
        name="compteBancaireId"
        label="Compte bancaire"
        required
        items={bankAccounts.options}
        disabled={disabled || !values.beneficiairePersonneId}
        placeholder={
          !values.beneficiairePersonneId
            ? "Sélectionner d'abord un bénéficiaire"
            : bankAccounts.options.length
              ? 'Sélectionner un compte'
              : 'Aucun compte disponible'
        }
      />
    </div>
  );
};

export default CreateTunnelModal;
